import * as React from 'react';
import { storiesOf } from '@storybook/react';
import { IconButton } from '..';
import { IconButtonThemes as Themes } from './types';
import {
  Favorite,
  FavoriteFill,
  LikeHeart,
  LikeHeartFill,
} from '@wix/wix-ui-icons-common/on-stage';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import { classes } from './IconButton.visual.st.css';
import { visualize, story, snap } from 'storybook-snapper';
import { VisualAsync, EditorShell } from '../../test/visual/VisualAsync';
import { iconButtonDriverFactory } from './IconButton.driver';

class IconButtonVisual extends React.Component<any> {
  render() {
    return (
      <VisualTestContainer>
        <IconButton icon={<Favorite />} {...this.props} />
        <IconButton icon={<FavoriteFill />} {...this.props} />
        <IconButton icon={<LikeHeart />} {...this.props} />
        <IconButton icon={<LikeHeartFill />} {...this.props} />
      </VisualTestContainer>
    );
  }
}

const noneTheme = {
  describe: 'none theme',
  its: [
    {
      it: 'default',
      props: {},
    },
    {
      it: 'disabled',
      props: {
        disabled: true,
      },
    },
  ],
};

const boxTheme = {
  describe: 'box theme',
  its: [
    {
      it: 'default',
      props: { theme: Themes.Box },
    },
    {
      it: 'disabled',
      props: {
        theme: Themes.Box,
        disabled: true,
      },
    },
    {
      it: 'custom style',
      props: {
        theme: Themes.Box,
        className: classes.box,
      },
    },
  ],
};

const tests = [
  {
    ...noneTheme,
    describe: 'none theme / as button',
  },
  {
    ...noneTheme,
    describe: 'none theme / as anchor',
    its: noneTheme.its.map(({ it, props }) => ({
      it,
      props: { ...props, as: 'a' },
    })),
  },
  {
    ...boxTheme,
    describe: 'box theme / as button',
  },
  {
    ...boxTheme,
    describe: 'box theme / as anchor',
    its: boxTheme.its.map(({ it, props }) => ({
      it,
      props: { ...props, as: 'a' },
    })),
  },
];

const focusTests = tests.map(({ describe, its }) => ({
  describe: `${describe} / focus`,
  its,
}));

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props }) => {
    storiesOf(`IconButton/${describe}`, module).add(it, () => (
      <EditorShell checkered>
        <IconButtonVisual {...props} />
      </EditorShell>
    ));
  });
});

const WstTest = ({
  variables,
}: {
  variables: Record<`--wst-${string}`, string>;
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      padding: '20px',
      ...variables,
    }}
  >
    <IconButton icon={<Favorite />} />
    <IconButton icon={<LikeHeart />} disabled />
    <IconButton icon={<Favorite />} theme={Themes.Box} />
    <IconButton icon={<LikeHeart />} theme={Themes.Box} disabled />
  </div>
);

visualize('IconButton', () => {
  focusTests.forEach(({ describe, its }) => {
    story(`${describe}`, () => {
      its.forEach(({ it, props }) => {
        snap(`${it}`, (done) => (
          <VisualAsync
            checkered
            driverFactory={iconButtonDriverFactory}
            onDone={done}
            // driver.click doesn't trigger focus ring for some reason
            action={(driver) => driver.element().then((el) => el.focus())}
          >
            <IconButton icon={<Favorite />} {...props} />
          </VisualAsync>
        ));
      });
    });
  });

  story('wst variables', () => {
    snap(
      'paragraph 2',
      <WstTest
        variables={{
          '--wst-paragraph-2-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'primary background',
      <WstTest
        variables={{
          '--wst-primary-background-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'system disabled',
      <WstTest
        variables={{
          '--wst-system-disabled-color-rgb': '0,255,0',
        }}
      />,
    );
  });
});
